import React from 'react';
import {
  Box,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { tokens } from '../../theme';
import 'mathlive';
import {
  defaultMultilineConfig,
  valuesMatch,
  getRowLabel,
  getRowType,
  ROW_TYPES,
} from './multilineUtils';

const EMPTY_ANSWER = { rows: [] };

/**
 * MultilineReadOnly Component
 * Displays submitted multiline answers in read-only mode.
 * Shows correctness indicators per row for auto-graded modes.
 */
const MultilineReadOnly = ({
  config = defaultMultilineConfig,
  studentAnswer = { rows: [] },
  correctAnswer = null,
  isCorrect = null,
  showCorrect = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const safeStudentAnswer = studentAnswer || EMPTY_ANSWER;
  const studentRows = safeStudentAnswer.rows || [];
  const correctRows = correctAnswer?.rows || [];
  const isAutoGraded = config.gradingMode === 'exactSet' || config.gradingMode === 'unorderedMatch';

  // Per-row valuesMatch: when allowTextInput is on and the two rows have
  // different types, they cannot match (math row never satisfies a text
  // correct-answer and vice versa). When the flag is off we deliberately
  // ignore any stale `row.type` and fall back to `config.inputType` —
  // mirrors server `multilineValuesMatch` so client correctness highlights
  // never disagree with server grading.
  const matchRows = (sr, cr) => {
    if (config.allowTextInput) {
      const sType = getRowType(sr, config);
      const cType = getRowType(cr, config);
      if (sType !== cType) return false;
      return valuesMatch(sr.value, cr.value, config.inputType, config.tolerance, config.caseSensitive, cType);
    }
    return valuesMatch(sr.value, cr.value, config.inputType, config.tolerance, config.caseSensitive);
  };

  // For auto-graded modes, determine which student rows match a correct row
  const getRowCorrectness = (studentRow) => {
    if (!isAutoGraded || correctRows.length === 0) return null;
    return correctRows.some(cr => matchRows(studentRow, cr));
  };

  // Find correct rows missing from student answers
  const getMissingCorrectRows = () => {
    if (!isAutoGraded || correctRows.length === 0) return [];
    return correctRows.filter(cr =>
      !studentRows.some(sr => matchRows(sr, cr))
    );
  };

  const renderValue = (row) => {
    const rowType = getRowType(row, config);
    if (rowType === ROW_TYPES.MATH) {
      const expected = row.value || '';
      return (
        <math-field
          read-only
          // Imperative sync — math-field doesn't pick up `value` attribute
          // changes after mount, so without setValue here a re-render from
          // an edited correct-answer would keep showing the prior value.
          ref={(el) => {
            if (el && el.getValue && el.getValue() !== expected) {
              el.setValue(expected);
            }
          }}
          style={{
            display: 'inline-block',
            verticalAlign: 'middle',
            border: 'none',
            background: 'transparent',
            fontSize: '16px',
            color: 'inherit',
          }}
        />
      );
    }
    return (
      <Typography variant="body1" component="span">
        {row.value || '(empty)'}
      </Typography>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Overall correctness badge (for manual grading or overall result) */}
      {isCorrect !== null && (
        <Box sx={{ mb: 1 }}>
          <Chip
            icon={isCorrect ? <CheckCircleIcon /> : <CancelIcon />}
            label={isCorrect ? 'Correct' : 'Incorrect'}
            color={isCorrect ? 'success' : 'error'}
            size="small"
          />
        </Box>
      )}

      {/* Student answer rows */}
      {studentRows.map((row, index) => {
        const label = getRowLabel(config, index);
        const rowCorrect = getRowCorrectness(row);

        return (
          <Box
            key={row.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.5,
              p: 0.5,
              pl: 1,
              borderRadius: '4px',
              backgroundColor: isAutoGraded && rowCorrect !== null
                ? rowCorrect
                  ? theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)'
                  : theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)'
                : 'transparent',
            }}
          >
            {/* Row label */}
            {label && (
              <Typography variant="body2" sx={{ minWidth: 30, fontWeight: 'bold', color: colors.grey[300] }}>
                {label}
              </Typography>
            )}

            {/* Value */}
            <Box sx={{ flex: 1 }}>
              {renderValue(row)}
            </Box>

            {/* Per-row correctness icon */}
            {isAutoGraded && rowCorrect !== null && (
              rowCorrect
                ? <CheckCircleIcon color="success" fontSize="small" />
                : <CancelIcon color="error" fontSize="small" />
            )}
          </Box>
        );
      })}

      {/* Show missing correct answers */}
      {showCorrect && isAutoGraded && (() => {
        const missing = getMissingCorrectRows();
        if (missing.length === 0) return null;
        return (
          <Box sx={{ mt: 1, p: 1, borderRadius: '4px', backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)', borderLeft: `3px solid ${theme.palette.success.main}` }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.palette.success.main, mb: 0.5, display: 'block' }}>
              Missing correct answers:
            </Typography>
            {missing.map((row, index) => (
              <Box key={row.id || index} sx={{ ml: 1, mb: 0.5 }}>
                {renderValue(row)}
              </Box>
            ))}
          </Box>
        );
      })()}

      {/* Empty state */}
      {studentRows.length === 0 && (
        <Typography variant="body2" sx={{ color: colors.grey[400], fontStyle: 'italic' }}>
          No answer provided
        </Typography>
      )}
    </Box>
  );
};

export default MultilineReadOnly;
